import { Badge } from "@car/ui/components/badge";
import { buttonVariants } from "@car/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@car/ui/components/dropdown-menu";
import { cn } from "@car/ui/lib/utils";
import { ArrowDown01Icon, Call02Icon, Route01Icon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";

import { openConsult } from "@/lib/consult";
import { telHref, type SiteBranch } from "@/lib/content";
import { toFaDigits } from "@/lib/format";

/** کارت یک شعبه (صفحه اصلی و صفحه تماس): نشانی، ساعت‌ها، خدمات، تماس و مسیریابی */
export function BranchCard({ branch }: { branch: SiteBranch }) {
  return (
    <article className="home-branch">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="home-branch-name">{branch.name}</h3>
          {branch.dealership ? (
            <p className="mt-1 flex flex-wrap items-center gap-1.5 text-sm text-muted-foreground">
              <Badge variant="secondary">نمایندگی</Badge>
              زیرمجموعه‌ی تابان خودرو
            </p>
          ) : null}
        </div>
        {branch.phone ? (
          <Badge variant="outline" dir="ltr">
            {toFaDigits(branch.phone)}
          </Badge>
        ) : null}
      </div>
      <p className="text-sm leading-6 text-muted-foreground">{branch.address}</p>
      <div className="home-branch-hours">
        <span>فروش: {branch.salesHours}</span>
        <span>خدمات: {branch.serviceHours}</span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {branch.services.length === 0 ? (
          <span className="text-sm text-muted-foreground">
            این شعبه فقط فروش دارد؛ برای خدمات شعبه دیگری را انتخاب کنید.
          </span>
        ) : (
          branch.services.map((service) => (
            <Badge key={service.key} variant="secondary">
              {service.label}
            </Badge>
          ))
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {branch.phone ? (
          <a href={telHref(branch.phone)} className={cn(buttonVariants({ size: "lg" }), "px-5")}>
            <HugeiconsIcon icon={Call02Icon} strokeWidth={1.8} />
            {branch.dealership ? "تماس با نمایندگی" : "تماس با شعبه"}
          </a>
        ) : (
          // شعبه‌ی بی‌تلفن: کارشناس تماس می‌گیرد؛ فرم همین‌جا باز می‌شود
          <button
            type="button"
            aria-haspopup="dialog"
            onClick={openConsult}
            className={cn(buttonVariants({ size: "lg" }), "px-5")}
          >
            <HugeiconsIcon icon={Call02Icon} strokeWidth={1.8} />
            درخواست تماس
          </button>
        )}
        <Directions branch={branch} />
      </div>
    </article>
  );
}

const directionsClass = cn(buttonVariants({ variant: "outline", size: "lg" }), "px-4");

/** مسیریابی با نشان، بلد یا نقشه گوگل؛ شعبه‌ی بی‌پین فقط نشانی را در نقشه گوگل جستجو می‌کند */
function Directions({ branch }: { branch: SiteBranch }) {
  if (branch.maps.length === 0) {
    return (
      <a
        href={`https://maps.google.com/?q=${encodeURIComponent(branch.address)}`}
        target="_blank"
        rel="noreferrer"
        className={directionsClass}
        aria-label={`مسیریابی به ${branch.name} در نقشه گوگل`}
      >
        <HugeiconsIcon icon={Route01Icon} strokeWidth={1.8} />
        مسیریابی
      </a>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <button
            type="button"
            className={directionsClass}
            aria-label={`مسیریابی به ${branch.name}`}
          />
        }
      >
        <HugeiconsIcon icon={Route01Icon} strokeWidth={1.8} />
        مسیریابی
        <HugeiconsIcon icon={ArrowDown01Icon} strokeWidth={1.8} className="size-4" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-48" lang="fa" dir="rtl">
        {branch.maps.map((map) => (
          <DropdownMenuItem
            key={map.label}
            className="min-h-11"
            render={<a href={map.href} target="_blank" rel="noreferrer" />}
          >
            با {map.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
